'use client';

import { FC, useState } from 'react';

import { useContentfulLiveUpdates } from '@contentful/live-preview/react';
import { Container } from '@purpur/library';
import { Icon } from '@purpur/library/icon';
import { chevronDown } from '@purpur/library/icon/assets/chevron-down';
import { chevronUp } from '@purpur/library/icon/assets/chevron-up';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { RichTextContent } from '@telialt/common-rich-text-content/src/RichTextContent';
import { useComponentId } from '@telialt/utils/src/hooks';

import { BlockLegalTextType } from '../../models/Content';
import { getBlockId } from '../../utils/content';
import {
    BlockLegalTextSection,
    DescriptionContainer,
    DescriptionWrapper,
    ExpandButton,
    LegalTextHeading,
} from './BlockLegalText.styles';
import { BLOCK_LEGAL_TEXT_TEST_IDS } from './test-ids';
import { transformBlockLegalText } from './transformers';

type Props = {
    contentItem: BlockLegalTextType;
};

export const BlockLegalText: FC<Props> = ({ contentItem: initialContentItem }) => {
    const { t } = useTranslations();

    const contentItem = useContentfulLiveUpdates(initialContentItem);
    const legalTextData = transformBlockLegalText(contentItem);

    const headingId = useComponentId(`${legalTextData.id}-heading`);
    const contentId = useComponentId(`${legalTextData.id}-content`);

    const [isExpanded, setIsExpanded] = useState(!legalTextData.isExpandable);

    return (
        <BlockLegalTextSection
            $background={legalTextData.background}
            $textColor={legalTextData.textColor}
            id={getBlockId(contentItem)}
        >
            <Container>
                <LegalTextHeading tag="h2" variant="title-100" id={headingId} negative={legalTextData.isNegative}>
                    {legalTextData.title}
                </LegalTextHeading>
                <DescriptionWrapper
                    $isExpanded={isExpanded}
                    aria-labelledby={headingId}
                    aria-hidden={!isExpanded}
                    role="region"
                    id={contentId}
                >
                    <DescriptionContainer>
                        <RichTextContent
                            richText={legalTextData.description}
                            isNegative={legalTextData.isNegative}
                            paragraphVariant="additional-100"
                        />
                    </DescriptionContainer>
                </DescriptionWrapper>
                {legalTextData.isExpandable && (
                    <ExpandButton
                        variant="text"
                        negative={legalTextData.isNegative}
                        onClick={() => setIsExpanded(!isExpanded)}
                        aria-expanded={isExpanded}
                        aria-controls={contentId}
                        data-test={BLOCK_LEGAL_TEXT_TEST_IDS.EXPAND_BUTTON}
                    >
                        {isExpanded ? t['content.block_legal_text.collapse'] : t['content.block_legal_text.expand']}
                        <Icon svg={isExpanded ? chevronUp : chevronDown} size="xs" />
                    </ExpandButton>
                )}
            </Container>
        </BlockLegalTextSection>
    );
};
